import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useAuth } from "./AuthContext";
import { getWishlist, addToWishlist, removeFromWishlist } from "../api/wishlistService";

const WishlistContext = createContext(null);

export function WishlistProvider({ children }) {
  const { isAuthenticated, openLoginModal } = useAuth();
  const [items, setItems] = useState([]);
  const [busyIds, setBusyIds] = useState(() => new Set());

  const refresh = useCallback(() => {
    if (!isAuthenticated) {
      setItems([]);
      return Promise.resolve();
    }
    return getWishlist()
      .then(setItems)
      .catch(() => {});
  }, [isAuthenticated]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const itemCount = items.length;

  const isWishlisted = useCallback((productId) => items.some((item) => item.productId === productId), [items]);

  const toggleWishlist = useCallback(
    async (productId) => {
      if (!isAuthenticated) {
        openLoginModal();
        return;
      }
      if (busyIds.has(productId)) return;

      const existing = items.find((item) => item.productId === productId);
      setBusyIds((prev) => new Set(prev).add(productId));

      try {
        if (existing) {
          await removeFromWishlist(productId);
          setItems((prev) => prev.filter((item) => item.productId !== productId));
        } else {
          const added = await addToWishlist(productId);
          setItems((prev) => [added, ...prev]);
        }
      } catch {
        // no local state changed yet on failure, nothing to roll back
      } finally {
        setBusyIds((prev) => {
          const next = new Set(prev);
          next.delete(productId);
          return next;
        });
      }
    },
    [isAuthenticated, openLoginModal, items, busyIds],
  );

  async function removeItem(productId) {
    await removeFromWishlist(productId);
    setItems((prev) => prev.filter((item) => item.productId !== productId));
  }

  const value = { items, itemCount, isWishlisted, toggleWishlist, removeItem, refresh };

  return <WishlistContext.Provider value={value}>{children}</WishlistContext.Provider>;
}

export function useWishlist() {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error("useWishlist must be used within a WishlistProvider");
  }
  return context;
}
